import { useCallback, useState } from "react";

import {
  pharmaConsentService,
  type GrantOrganizationConsentsResult,
  type PharmaConsentPreparation,
  type PharmaOrganizationItem,
} from "../services/pharmaConsentService";

export type UsePharmaConsentForPurchaseResult = {
  isPreparing: boolean;
  isGranting: boolean;
  prepareError: string | null;
  preparation: PharmaConsentPreparation | null;
  prepare: (patientId: string) => Promise<PharmaConsentPreparation>;
  grantSelected: (params: {
    patientId: string;
    institutionIds: string[];
  }) => Promise<GrantOrganizationConsentsResult>;
  reset: () => void;
};

/**
 * Hook de domínio para o fluxo de consentimento pharma na compra.
 * UI/legado deve usar este hook — não chamar o service direto.
 */
export function usePharmaConsentForPurchase(): UsePharmaConsentForPurchaseResult {
  const [isPreparing, setIsPreparing] = useState(false);
  const [isGranting, setIsGranting] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [preparation, setPreparation] =
    useState<PharmaConsentPreparation | null>(null);

  const reset = useCallback(() => {
    setPreparation(null);
    setPrepareError(null);
    setIsPreparing(false);
    setIsGranting(false);
  }, []);

  const prepare = useCallback(async (patientId: string) => {
    setIsPreparing(true);
    setPrepareError(null);
    try {
      const result =
        await pharmaConsentService.preparePharmaConsentSelection(patientId);
      setPreparation(result);
      return result;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Não foi possível carregar as farmácias para consentimento.";
      setPrepareError(message);
      throw err;
    } finally {
      setIsPreparing(false);
    }
  }, []);

  const grantSelected = useCallback(
    async (params: { patientId: string; institutionIds: string[] }) => {
      setIsGranting(true);
      try {
        return await pharmaConsentService.grantOrganizationConsents(params);
      } finally {
        setIsGranting(false);
      }
    },
    [],
  );

  return {
    isPreparing,
    isGranting,
    prepareError,
    preparation,
    prepare,
    grantSelected,
    reset,
  };
}

export type { PharmaOrganizationItem, PharmaConsentPreparation };
